import { ApolloLink, Observable } from '@apollo/client'
import { getOperationDefinition } from '@apollo/client/utilities'
import * as Sentry from '@sentry/gatsby'
import { getUser } from '../services/auth'

// Valor de SPAN_STATUS_ERROR en @sentry/core (no lo reexporta @sentry/gatsby).
const SPAN_STATUS_ERROR = 2
const MAX_MESSAGE_LENGTH = 300

// Los errores con extensions.type salen del catálogo del backend (reglas de
// negocio): quedan en el span y en un breadcrumb, pero no abren issue.
const isBusinessError = error => Boolean(error?.extensions?.type)

// El mensaje solo viaja en los errores de negocio, que son copy del catálogo.
// Los inesperados traen el SQL del backend con valores (emails, teléfonos):
// el detalle queda en el issue del backend, que comparte la traza.
const describeError = error => ({
  ...(isBusinessError(error) && {
    message: String(error.message || '').slice(0, MAX_MESSAGE_LENGTH),
  }),
  code: error?.extensions?.code,
  type: error?.extensions?.type,
  path: error?.path?.join('.'),
})

// Al recargar o salir de la página el navegador corta los fetch en vuelo y
// Apollo los reporta como "Failed to fetch": no es una falla de red real.
let pageUnloading = false
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    pageUnloading = true
  })
}

// Solo el id: nombre, teléfono y email del beneficiario no se envían.
const syncSentryUser = () => {
  const id = getUser()?.user?.id
  Sentry.setUser(id ? { id: String(id) } : null)
}

const reportGraphQLErrors = (operationInfo, errors) => {
  const details = errors.map(describeError)
  Sentry.addBreadcrumb({
    category: 'graphql.error',
    level: 'error',
    message: operationInfo.label,
    data: { errors: details },
  })

  const unexpected = errors.filter(error => !isBusinessError(error))
  if (!unexpected.length) {
    return
  }
  const [first] = unexpected.map(describeError)
  Sentry.withScope(scope => {
    scope.setTag('graphql.operation', operationInfo.name)
    scope.setTag('graphql.operation_type', operationInfo.type)
    if (first.code) {
      scope.setTag('graphql.error_code', first.code)
    }
    scope.setContext('graphql', { ...operationInfo, errors: details })
    scope.setFingerprint([
      'graphql',
      operationInfo.name,
      first.code || 'unknown',
    ])
    const error = new Error(
      `${operationInfo.label}: ${first.code || 'GraphQL error'}${
        first.path ? ` (${first.path})` : ''
      }`,
    )
    error.name = 'GraphQLError'
    Sentry.captureException(error)
  })
}

const reportNetworkError = (operationInfo, networkError) => {
  if (pageUnloading) {
    return
  }
  Sentry.withScope(scope => {
    scope.setTag('graphql.operation', operationInfo.name)
    scope.setTag('graphql.operation_type', operationInfo.type)
    scope.setTag('graphql.network_error', true)
    if (networkError?.statusCode) {
      scope.setTag('http.status_code', networkError.statusCode)
    }
    scope.setContext('graphql', operationInfo)
    scope.setFingerprint(['graphql-network', operationInfo.name])
    Sentry.captureException(networkError)
  })
}

// Cada operación abre su propia transacción dentro de la traza de la página:
// el fetch de HttpLink (que se dispara sincrónicamente al suscribirse) queda
// como hijo y propaga la traza al backend aunque ya haya terminado el
// pageload. Por eso va justo antes de httpLink y después de authLink, que es
// asíncrono. forceTransaction hace falta porque en el navegador Sentry cuelga
// todo span del span raíz activo (parentSpanIsAlwaysRootSpan).
export const sentryLink = new ApolloLink((operation, forward) => {
  const type = getOperationDefinition(operation.query)?.operation || 'unknown'
  const name = operation.operationName || 'anonymous'
  const operationInfo = { name, type, label: `${type} ${name}` }

  syncSentryUser()

  return new Observable(observer => {
    let subscription
    let span
    let ended = false
    const endSpan = () => {
      if (span && !ended) {
        ended = true
        span.end()
      }
    }
    Sentry.startSpanManual(
      {
        name: operationInfo.label,
        op: 'graphql.client',
        forceTransaction: true,
        attributes: {
          'graphql.operation.name': name,
          'graphql.operation.type': type,
        },
      },
      activeSpan => {
        span = activeSpan
        subscription = forward(operation).subscribe({
          next: result => {
            if (result?.errors?.length) {
              const [first] = result.errors.map(describeError)
              span.setStatus({
                code: SPAN_STATUS_ERROR,
                message: first.type ? 'failed_precondition' : 'internal_error',
              })
              span.setAttributes({
                'graphql.error_code': first.code,
                'graphql.error_type': first.type,
              })
              Sentry.withActiveSpan(span, () =>
                reportGraphQLErrors(operationInfo, result.errors),
              )
            }
            observer.next(result)
          },
          error: networkError => {
            span.setStatus({ code: SPAN_STATUS_ERROR, message: 'unavailable' })
            Sentry.withActiveSpan(span, () =>
              reportNetworkError(operationInfo, networkError),
            )
            endSpan()
            observer.error(networkError)
          },
          complete: () => {
            endSpan()
            observer.complete()
          },
        })
      },
    )
    return () => {
      if (subscription) {
        subscription.unsubscribe()
      }
      endSpan()
    }
  })
})
